
(() => {
  const imageCache = new Map();

  const defaults = {
    left: {
      wood: 'black-walnut',
      epoxy: 'midnight-black'
    },
    right: {
      wood: 'walnut',
      epoxy: 'midnight-black'
    }
  };

  const comboKey = (wood, epoxy) => `${wood}::${epoxy}`;

  function preload(url) {
    if (!imageCache.has(url)) {
      const task = new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = async () => {
          try {
            if (img.decode) await img.decode();
          } catch (_) {}
          resolve(url);
        };

        img.onerror = () => {
          reject(new Error('Image unavailable'));
        };

        img.src = url;
      });

      imageCache.set(url, task);
      task.catch(() => imageCache.delete(url));
    }

    return imageCache.get(url);
  }

  class MaterialCompare {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.mode || 'full';

      this.data = JSON.parse(
        root.querySelector('[data-swmc-data]').textContent
      );

      this.data.woods.sort(
        (a, b) => Number(a.order) - Number(b.order)
      );

      this.data.epoxies.sort(
        (a, b) => Number(a.order) - Number(b.order)
      );

      this.map = new Map(
        this.data.combinations
          .filter(c => c.wood && c.epoxy)
          .map(c => [
            comboKey(c.wood, c.epoxy),
            c.image
          ])
      );

      this.state = JSON.parse(JSON.stringify(defaults));
      this.active = 'left';

      this.tokens = {
        left: 0,
        right: 0
      };

      this.imageStates = {
        left: 'pending',
        right: 'pending'
      };

      this.loaded = false;
      this.isOpen = false;
      this.lastFocused = null;

      this.range = root.querySelector('[data-range]');
      this.divider = root.querySelector('[data-divider]');
      this.leftLayer = root.querySelector(
        '[data-image-side="left"]'
      );
      this.status = root.querySelector('[data-status]');

      this.trigger = root.querySelector('[data-drawer-open]');
      this.overlay = root.querySelector('[data-drawer-overlay]');
      this.panel = root.querySelector('[data-drawer-panel]');

      this.onRange = () => this.updateDivider();
      this.onClick = e => this.handleClick(e);
      this.onKeydown = e => this.handleKeydown(e);

      this.range.addEventListener('input', this.onRange);
      root.addEventListener('click', this.onClick);

      this.updateDivider();
      this.render();

      if (this.mode === 'drawer') {
        /*
          Move the fixed drawer interface to body so
          transformed or clipped Gem theme containers
          do not interfere with fixed positioning.

          The data and section remain in Shopify's
          original section wrapper.
        */

        document.addEventListener(
          'keydown',
          this.onKeydown
        );
      } else {
        this.observeImages();
      }
    }

    mountDrawer() {
      if (!this.trigger || !this.overlay) return;

      this.originalTriggerParent = this.trigger.parentNode;
      this.originalOverlayParent = this.overlay.parentNode;

      this.triggerMarker = document.createComment(
        'swmc trigger position'
      );

      this.overlayMarker = document.createComment(
        'swmc overlay position'
      );

      this.originalTriggerParent.insertBefore(
        this.triggerMarker,
        this.trigger
      );

      this.originalOverlayParent.insertBefore(
        this.overlayMarker,
        this.overlay
      );

      document.body.appendChild(this.trigger);
      document.body.appendChild(this.overlay);

      /*
        When drawer elements are portaled to body,
        click handling must also be attached there.
      */

      this.trigger.addEventListener(
        'click',
        this.onClick
      );

      this.overlay.addEventListener(
        'click',
        this.onClick
      );
    }

    observeImages() {
      if (this.loaded) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          entries => {
            if (entries.some(e => e.isIntersecting)) {
              this.startImages();
              this.observer.disconnect();
            }
          },
          { rootMargin: '350px 0px' }
        );

        this.observer.observe(this.root);
      } else {
        this.startImages();
      }
    }

    openDrawer() {
      if (
        this.mode !== 'drawer' ||
        !this.overlay ||
        this.isOpen
      ) return;

      /*
        Close another material drawer if one is open.
      */

      document.querySelectorAll(
        '.swmc__overlay:not([hidden])'
      ).forEach(overlay => {
        if (overlay !== this.overlay) {
          const owner = overlay._swmcOwner;
          owner?.closeDrawer();
        }
      });

      this.lastFocused = document.activeElement;
      this.isOpen = true;

      this.overlay.hidden = false;
      this.overlay._swmcOwner = this;

      this.trigger?.setAttribute(
        'aria-expanded',
        'true'
      );

      this.previousBodyOverflow =
        document.body.style.overflow;

      document.body.style.overflow = 'hidden';

      if (!this.loaded) {
        this.startImages();
      }

      this.panel?.focus();
    }

    closeDrawer() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.overlay.hidden = true;

      this.trigger?.setAttribute(
        'aria-expanded',
        'false'
      );

      document.body.style.overflow =
        this.previousBodyOverflow || '';

      if (
        this.lastFocused &&
        this.lastFocused.isConnected
      ) {
        this.lastFocused.focus();
      } else {
        this.trigger?.focus();
      }
    }

    handleKeydown(e) {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDrawer();
        return;
      }

      if (e.key !== 'Tab' || !this.panel) return;

      const focusable = Array.from(
        this.panel.querySelectorAll(
          'button:not([disabled]), ' +
          'input:not([disabled]), ' +
          'a[href], select:not([disabled]), ' +
          'textarea:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.getClientRects().length);

      if (!focusable.length) {
        e.preventDefault();
        this.panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (e.shiftKey) {
        if (current === first || current === this.panel) {
          e.preventDefault();
          last.focus();
        }
      } else if (
        current === last ||
        current === this.panel
      ) {
        e.preventDefault();
        first.focus();
      }
    }

    updateDivider() {
      const p = Number(this.range.value);

      this.leftLayer.style.clipPath =
        `inset(0 ${100 - p}% 0 0)`;

      this.divider.style.left = `${p}%`;
    }

    label(kind, code) {
      return (
        this.data[kind].find(x => x.code === code) || {}
      ).name || code;
    }

    render() {
      for (const side of ['left', 'right']) {
        const button = this.root.querySelector(
          `[data-side-button="${side}"]`
        );

        button.classList.toggle(
          'is-active',
          side === this.active
        );

        button.setAttribute(
          'aria-pressed',
          String(side === this.active)
        );

        const s = this.state[side];

        this.root.querySelector(
          `[data-side-summary="${side}"]`
        ).textContent =
          `${this.label('woods', s.wood)} · ` +
          `${this.label('epoxies', s.epoxy)}`;
      }

      const current = this.state[this.active];

      this.root.querySelector(
        '[data-current-wood]'
      ).textContent =
        this.label('woods', current.wood);

      this.root.querySelector(
        '[data-current-epoxy]'
      ).textContent =
        this.label('epoxies', current.epoxy);

      this.renderOptions(
        'woods',
        'wood',
        '[data-wood-options]'
      );

      this.renderOptions(
        'epoxies',
        'epoxy',
        '[data-epoxy-options]'
      );
    }

    renderOptions(list, kind, selector) {
      const host = this.root.querySelector(selector);

      host.replaceChildren();

      for (const item of this.data[list]) {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'swmc__swatch';

        button.dataset.kind = kind;
        button.dataset.code = item.code;

        const selected =
          this.state[this.active][kind] === item.code;

        button.classList.toggle(
          'is-selected',
          selected
        );

        button.setAttribute(
          'aria-pressed',
          String(selected)
        );

        button.setAttribute(
          'aria-label',
          item.name
        );

        const visual = document.createElement('span');
        visual.className = 'swmc__swatch-visual';

        if (item.swatch) {
          visual.style.backgroundImage =
            `url(${JSON.stringify(item.swatch)})`;
        }

        const label = document.createElement('span');
        label.className = 'swmc__swatch-label';
        label.textContent = item.name;

        button.append(visual, label);
        host.append(button);
      }
    }

    handleClick(e) {
      const open = e.target.closest('[data-drawer-open]');

      if (open && open === this.trigger) {
        this.openDrawer();
        return;
      }

      const close = e.target.closest('[data-drawer-close]');

      if (close && this.overlay?.contains(close)) {
        this.closeDrawer();
        return;
      }

      const sideButton = e.target.closest(
        '[data-side-button]'
      );

      if (sideButton && this.root.contains(sideButton)) {
        this.active = sideButton.dataset.sideButton;
        this.render();
        return;
      }

      const swap = e.target.closest('[data-swap]');

      if (swap && this.root.contains(swap)) {
        [
          this.state.left,
          this.state.right
        ] = [
          this.state.right,
          this.state.left
        ];

        this.render();

        if (this.loaded) {
          this.updateImage('left');
          this.updateImage('right');
        }

        return;
      }

      const option = e.target.closest(
        '[data-kind][data-code]'
      );

      if (option && this.root.contains(option)) {
        this.state[this.active][option.dataset.kind] =
          option.dataset.code;

        this.render();

        if (this.loaded) {
          this.updateImage(this.active);
        }
      }
    }

    startImages() {
      if (this.loaded) return;

      this.loaded = true;
      this.updateImage('left');
      this.updateImage('right');
    }

    updateStatus() {
      if (
        this.imageStates.left === 'error' ||
        this.imageStates.right === 'error'
      ) {
        this.status.textContent =
          'One or more preview images could not load.';
      } else if (
        this.imageStates.left === 'missing' ||
        this.imageStates.right === 'missing'
      ) {
        this.status.textContent =
          'Some material combinations do not have preview images yet.';
      } else {
        this.status.textContent = '';
      }
    }

    async updateImage(side) {
      const token = ++this.tokens[side];

      const s = this.state[side];

      const url = this.map.get(
        comboKey(s.wood, s.epoxy)
      );

      const img = this.root.querySelector(
        `[data-image="${side}"]`
      );

      const placeholder = this.root.querySelector(
        `[data-placeholder="${side}"]`
      );

      const name =
        `${this.label('woods', s.wood)} + ` +
        `${this.label('epoxies', s.epoxy)}`;

      if (!url) {
        img.hidden = true;
        img.removeAttribute('src');

        placeholder.hidden = false;
        placeholder.textContent =
          `${name} · Preview coming soon`;

        this.imageStates[side] = 'missing';
        this.updateStatus();
        return;
      }

      this.imageStates[side] = 'loading';
      this.updateStatus();

      try {
        await preload(url);

        if (
          token !== this.tokens[side] ||
          !this.root.isConnected
        ) return;

        img.src = url;
        img.alt = `${name} custom epoxy table`;
        img.hidden = false;
        placeholder.hidden = true;

        this.imageStates[side] = 'ready';
        this.updateStatus();
      } catch (_) {
        if (token !== this.tokens[side]) return;

        img.hidden = true;
        img.removeAttribute('src');

        placeholder.hidden = false;
        placeholder.textContent =
          `${name} · Preview unavailable`;

        this.imageStates[side] = 'error';
        this.updateStatus();
      }
    }

    destroy() {
      this.observer?.disconnect();

      this.closeDrawer();

      this.range.removeEventListener(
        'input',
        this.onRange
      );

      this.root.removeEventListener(
        'click',
        this.onClick
      );

      document.removeEventListener(
        'keydown',
        this.onKeydown
      );

      if (this.trigger) {
        this.trigger.removeEventListener(
          'click',
          this.onClick
        );
      }

      if (this.overlay) {
        this.overlay.removeEventListener(
          'click',
          this.onClick
        );
      }

      if (this.triggerMarker?.parentNode) {
        this.triggerMarker.parentNode.insertBefore(
          this.trigger,
          this.triggerMarker
        );
        this.triggerMarker.remove();
      }

      if (this.overlayMarker?.parentNode) {
        this.overlayMarker.parentNode.insertBefore(
          this.overlay,
          this.overlayMarker
        );
        this.overlayMarker.remove();
      }

      this.tokens.left++;
      this.tokens.right++;

      this.root._swmc = null;
    }
  }

  function getNodes(scope) {
    const nodes = [];

    if (scope.matches?.('[data-swmc-section]')) {
      nodes.push(scope);
    }

    scope.querySelectorAll?.(
      '[data-swmc-section]'
    ).forEach(n => nodes.push(n));

    return nodes;
  }

  function init(scope = document) {
    getNodes(scope).forEach(n => {
      if (!n._swmc) {
        try {
          n._swmc = new MaterialCompare(n);
        } catch (e) {
          console.error(
            'Seawave Material Comparison:',
            e
          );
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init()
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    e => init(e.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    e => {
      getNodes(e.target).forEach(
        n => n._swmc?.destroy()
      );
    }
  );
})();
